import React from 'react'
import { clsx } from 'clsx'
import type { ButtonProps } from '@/types'

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  children,
  className,
  type = 'button',
  title,
}) => {
  const baseClasses = [
    'inline-flex',
    'items-center',
    'justify-center',
    'font-medium',
    'transition-all',
    'duration-200',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    'disabled:pointer-events-none',
    'rounded-lg',
  ]

  const sizeClasses = {
    sm: ['text-sm', 'px-3', 'py-1.5', 'h-8'],
    md: ['text-base', 'px-4', 'py-2', 'h-10'],
    lg: ['text-lg', 'px-6', 'py-3', 'h-12'],
  }

  const variantClasses = {
    primary: [
      'bg-primary-600',
      'hover:bg-primary-700',
      'text-white',
      'border-transparent',
      'focus:ring-primary-500',
      'active:bg-primary-800',
    ],
    secondary: [
      'bg-secondary-600',
      'hover:bg-secondary-700',
      'text-white',
      'border-transparent',
      'focus:ring-secondary-500',
      'active:bg-secondary-800',
    ],
    outline: [
      'bg-transparent',
      'border-2',
      'border-primary-600',
      'text-primary-600',
      'hover:bg-primary-50',
      'focus:ring-primary-500',
      'active:bg-primary-100',
    ],
    ghost: [
      'bg-transparent',
      'text-secondary-700',
      'hover:bg-secondary-100',
      'focus:ring-secondary-500',
      'active:bg-secondary-200',
    ],
  }

  const loadingClasses = loading ? ['opacity-75', 'pointer-events-none'] : []

  const buttonClasses = clsx(
    baseClasses,
    sizeClasses[size],
    variantClasses[variant],
    loadingClasses,
    className
  )

  const handleClick = (): void => {
    if (!disabled && !loading && onClick) {
      onClick()
    }
  }

  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={disabled || loading}
      onClick={handleClick}
      title={title}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-3 h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  )
}

export default Button 